import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { StatusComponent } from '../../ui/status/status.component';
import { SearchInputComponent } from '../../inputs/search-input/search-input.component';
import { IMessageReportContacts } from '../../../pages/organization/campaigns/types/messagesTypes';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../inputs/sort-dropdown/sort-dropdown.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../inputs/status-filter/status-filter.component';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import {
  DeliveryFilter,
  FAILED_DELIVERY_STATES,
  IN_FLIGHT_DELIVERY_STATES,
} from '../../../services/campaign/campaign-insights.service';
import { formatCount, formatWhen } from '../../../../utils/sms';

export interface ReportDetailsTableOptions {
  name: string;
  id: string | number;
}

/** Sort keys from the sort menu (field,direction). */
export type TArrangementSort = string;

interface ReceiptRow {
  key: string | number;
  name: string;
  number: string;
  group: string;
  gender: string;
  age: string;
  country: string;
  status: string;
  /** Drives the dot colour: delivered, failed or pending. */
  tone: string;
  when: string;
}

/**
 * Who a campaign send reached: one row per recipient with their delivery
 * status, filterable by send, name or number, and delivery outcome.
 */
@Component({
  selector: 'app-campaign-report-details-table',
  standalone: true,
  imports: [
    StatusComponent,
    SearchInputComponent,
    SortDropdownComponent,
    StatusFilterComponent,
    RouterLink,
    EmptyStateComponent,
    DataTableComponent,
    PaginationComponent,
    HasPermissionDirective,
  ],
  templateUrl: './campaign-report-details-table.component.html',
  styleUrl: './campaign-report-details-table.component.css',
})
export class CampaignReportDetailsTableComponent implements OnChanges {
  /** Shows the first five rows and a link to the full list (when not paginated). */
  @Input() miniTable = true;
  @Input() routeId: number | null = null;
  @Input() tableTitle: string = '';
  @Input() loading: boolean = false;
  @Input() tableOptions: ReportDetailsTableOptions[] = [];
  @Input() tableData: IMessageReportContacts[] = [];
  @Input() selectedSort: TArrangementSort | '' = '';
  /** The send currently shown (an id from tableOptions). */
  @Input() selectedOption: string | number | null = null;
  @Input() statusFilter: DeliveryFilter = 'all';
  /** Paging: set totalPages to paginate the table. */
  @Input() currentPage = 0;
  @Input() totalPages: number | null = null;
  @Input() totalItems: number | null = null;
  @Input() pageSize = 20;
  /** Show a "Download CSV" action (still gated by export-campaign-reports). */
  @Input() exportable = false;
  @Input() exporting = false;
  @Input() exportNote = '';
  /** The export note reports a failure, so it is announced as an alert. */
  @Input() exportFailed = false;
  /** Search, filter or a send other than "all" is narrowing the rows. */
  @Input() filtered = false;

  @Output() search = new EventEmitter<string>();
  @Output() sort = new EventEmitter<TArrangementSort>();
  @Output() scheduleChange = new EventEmitter<string>();
  @Output() statusChange = new EventEmitter<DeliveryFilter>();
  @Output() changePage = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() export = new EventEmitter<void>();

  rows: ReceiptRow[] = [];

  readonly formatCount = formatCount;

  readonly statusOptions: StatusFilterOption[] = [
    { value: 'all', title: 'All recipients', icon: 'list', tone: 'neutral' },
    { value: 'delivered', title: 'Delivered', icon: 'check_circle', tone: 'success' },
    { value: 'failed', title: 'Failed', icon: 'error', tone: 'danger' },
    { value: 'inflight', title: 'No receipt yet', icon: 'schedule', tone: 'warning' },
  ];

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'fullName',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  get paginated(): boolean {
    return this.totalPages !== null;
  }

  /** "412 recipients match", or "Loading recipients" while a page loads. */
  get countLabel(): string {
    if (this.loading) return 'Loading recipients';
    if (this.totalItems === null) return '';
    const noun = this.totalItems === 1 ? 'recipient' : 'recipients';
    return `${formatCount(this.totalItems)} ${noun}${this.filtered ? ' match' : ''}`;
  }

  ngOnChanges() {
    const data = Array.isArray(this.tableData) ? this.tableData : [];
    const shown = this.miniTable && !this.paginated ? data.slice(0, 5) : data;
    this.rows = shown.filter((item) => !!item).map((item, i) => toRow(item, i));
  }

  optionChange(event: Event) {
    const selectedId = (event.target as HTMLSelectElement).value;
    this.scheduleChange.emit(selectedId);
  }

  searchTableData(searchTerm: string) {
    this.search.emit(searchTerm);
  }

  sortTableData(sortTerm: TArrangementSort) {
    this.sort.emit(sortTerm);
  }

  filterStatus(value: string | null) {
    const filter = (value || 'all') as DeliveryFilter;
    this.statusChange.emit(filter);
  }

  isSelected(option: ReportDetailsTableOptions): boolean {
    return String(option.id) === String(this.selectedOption ?? '');
  }
}

function sentence(value: unknown): string {
  const words = String(value ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

/** Receipt state → the word app-status colours by. */
function toneOf(status: string): string {
  const value = status.toUpperCase();
  if (value === 'DELIVERED') return 'delivered';
  if (FAILED_DELIVERY_STATES.includes(value)) return 'failed';
  if (IN_FLIGHT_DELIVERY_STATES.includes(value)) return 'pending';
  return value ? 'unknown' : 'pending';
}

function toRow(item: IMessageReportContacts, index: number): ReceiptRow {
  const status = String(item.deliveryStatus ?? '');
  const age = Number(item.age);
  return {
    key: item.id ?? item.uuid ?? index,
    name: String(item.fullName ?? '').trim() || 'No name',
    number: String(item.mobileNumber ?? '').trim() || '—',
    group: String(item.group ?? '').trim() || '—',
    gender: sentence(item.gender) || '—',
    age: Number.isFinite(age) && age > 0 ? String(age) : '—',
    country: String(item.country ?? '').trim() || '—',
    status: status || 'Pending',
    tone: toneOf(status),
    when: formatWhen(item.timeStamp),
  };
}
